/**
 * DfyReturnHandler — runs once on FanPortal load. If the URL carries
 * ?dfy_session=<id>, polls /api/dfy/checkout-status until order_status flips
 * to "paid_unassigned" (webhook fired) or we hit the polling cap.
 * Cleans up the URL on completion.
 *
 * Mirrors AffiliateReturnHandler; 5× poll at 2s intervals per playbook.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MAX_POLLS = 5;
const POLL_INTERVAL_MS = 2000;

export default function DfyReturnHandler() {
  const loc = useLocation();
  const nav = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sessionId = params.get("dfy_session");
    const cancelled = params.get("dfy_cancelled");

    const cleanUrl = (key) => {
      params.delete(key);
      nav(
        { pathname: loc.pathname, search: params.toString() ? `?${params.toString()}` : "" },
        { replace: true }
      );
    };

    if (cancelled) {
      toast("Done-for-You checkout cancelled — no charge made.", { icon: "↩️" });
      cleanUrl("dfy_cancelled");
      return;
    }
    if (!sessionId || handled.current) return;
    handled.current = true;

    let cancel = false;
    let attempt = 0;
    const tick = async () => {
      if (cancel) return;
      attempt += 1;
      try {
        const r = await api.get(`/dfy/checkout-status/${encodeURIComponent(sessionId)}`);
        const status = r.data?.order_status;
        if (status && status !== "pending_payment") {
          toast.success(
            "🎯 Payment received — we'll email you within 7–14 days when your twin is ready to claim."
          );
          cleanUrl("dfy_session");
          return;
        }
        if (attempt >= MAX_POLLS) {
          toast("Stripe is still processing your payment. We'll email confirmation shortly.", {
            icon: "⏳",
          });
          cleanUrl("dfy_session");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempt >= MAX_POLLS) {
          toast.error(
            e.response?.data?.detail ||
              "Could not verify DFY payment. Refresh or check your email for confirmation."
          );
          cleanUrl("dfy_session");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();

    return () => {
      cancel = true;
    };
  }, [loc.search, loc.pathname, nav]);

  return null;
}
